
























import {
  computed,
  ComputedRef,
  defineComponent,
  PropType,
  provide,
  toRefs,
  useContext,
  useStore
} from '@nuxtjs/composition-api'
import ServiceSearchCondition from '~/components/organisms/DS2/ServiceSearchCondition'
import SelectedSearchServiceConditions from '~/components/organisms/DS2/SelectedSearchServiceConditions'
import ServiceSearchHeaderFacet from '~/components/organisms/DS2/ServiceSearchHeaderFacet'
import { Category, ServiceSearchOption } from '~/stub/apigateway/master/master_pb'
import { SearchServiceCondition } from '~/stub/apigateway/service/search_pb'
import { ChangeConditionObject, ConditionOptionCommon } from '~/types/services/search'

const CATEGORY_TYPE_DB_KEY = 'master_category_type_id'
const CATEGORY_TYPE_QUERY_KEY = 'category_type_id'

const serviceSearchFormV2 = defineComponent({
  name: 'ServiceSearchFormV2',
  components: {
    ServiceSearchCondition,
    SelectedSearchServiceConditions,
    ServiceSearchHeaderFacet
  },
  props: {
    conditionOptionsCommon: {
      type: Array as PropType<ConditionOptionCommon[]>,
      default: () => []
    },
    attributes: {
      type: Object,
      default: () => ({})
    },
    condition: {
      type: Object as PropType<SearchServiceCondition.AsObject>,
      default: () => ({})
    },
    searchOptionMasters: {
      type: Array as PropType<ServiceSearchOption.AsObject[]>,
      default: () => []
    },
    parentCategoryId: {
      type: Number as PropType<number>,
      default: null
    },
    isFetching:{
      type: Boolean as PropType<boolean>,
      default: false
    },
    isShowEmphasisOnly: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },

  setup(props, context) {
    const { conditionOptionsCommon, attributes, searchOptionMasters, condition, parentCategoryId } = toRefs(props)
    const { $util, route } = useContext()
    const store = useStore()
    const isCategoryLocked = computed(() => {
      return !!route.value.params.categoryId || !!route.value.query.category_id
    })

    const masterCategories = computed(() => {
      return store.state['master'].masterCategories
    })

    const categoryMasterRef = computed<ComputedRef<Category.AsObject>>(() => {
      if(condition.value.masterCategoryId) {
        const parentCategory = condition.value.categoriesList[0]
        const parentCategoryMaster = masterCategories.value.find(c => c.id === parentCategory.id)
        if(parentCategory.isCurrent) {
          return parentCategoryMaster
        }
        return parentCategoryMaster?.subCategories.find(c => c.id === Number(condition.value.masterCategoryId))
      }
      return null
    })


    provide('categoryMasterRef', categoryMasterRef)
    provide('setOptionAttributes', setOptionAttributes)
    provide('condition', condition)
    provide('parentCategoryId', parentCategoryId)

    // 強調絞り込みとして表示する絞り込み条件の一覧
    const emphasizeConditionOptions = computed(() => {
      if (!conditionOptionsCommon.value.length) {
        return []
      }
      return conditionOptionsCommon.value
        .map(conditionOption => {
          const master = searchOptionMasters.value.find(
            e => e.keyCategoryId === conditionOption.master.keyCategoryId && e.emphasizeTypeWeb === 'radio'
          )
          // マスターの存在しない検索オプションは除外する
          if (!master) {
            return null
          }
          return {
            option: conditionOption.option,
            master
          }
        })
        .filter(x => !!x)
    })

    // 強調絞り込みとして表示するかどうかの判定
    function isShowEmphasizeConditionOption(key: string): boolean {
      const notSelectedEmphasizeConditionKey = conditionOptionsCommon.value.find(condition => {
        return (
          !condition.option.value &&
          !condition.option.valuesList.length &&
          condition.master.emphasizeTypeWeb === 'radio'
        )
      })?.master.key
      if (notSelectedEmphasizeConditionKey) {
        return notSelectedEmphasizeConditionKey === key
      }
      return false
    }

    // 絞り込み項目の表示地を切り替える
    function switchSearchConditionPosition(value: boolean): void {
      context.emit('switch-position', value)
    }

    function isFalsyOrEmpty(value): boolean {
      if (!value) {
        return true
      }
      if (Array.isArray(value)) {
        return value.length === 0
      }
      if (typeof value === 'object') {
        return Object.keys(value).length === 0
      }
      return false
    }

    /**
     * カテゴリ固有の検索条件変更
     * @param condition 絞り込み条件
     * @param emitChangeEvent 親コンポーネントにemitするか
     */
    function setOptionAttributes(condition: ChangeConditionObject, emitChangeEvent = true): void {
      const { key, value, categoryId } = condition
      setAttributes(key, value, false)
      // 関連カテゴリIDのある検索オプションのキーを抽出
      const categorizedOptionKeys = conditionOptionsCommon.value
        .filter(
          conditionOptionCommon =>
            conditionOptionCommon.option.relatedCategoryId && conditionOptionCommon.master.key
        )
        .map(conditionOptionCommon => conditionOptionCommon.master.key)

      // minKey、maxKeyを持つ検索オプションの対応
      conditionOptionsCommon.value
        .filter(
          conditionOptionCommon =>
            conditionOptionCommon.option.relatedCategoryId &&
            (conditionOptionCommon.master.minKey || conditionOptionCommon.master.maxKey)
        )
        .forEach(conditionOptionCommon => {
          if (conditionOptionCommon.master.minKey) {
            categorizedOptionKeys.push(conditionOptionCommon.master.minKey)
          }
          if (conditionOptionCommon.master.maxKey) {
            categorizedOptionKeys.push(conditionOptionCommon.master.maxKey)
          }
        })

      if (!isCategoryLocked.value && categorizedOptionKeys.includes(key) && isFalsyOrEmpty(value)) {
        const hasNoCategorizedOptions = categorizedOptionKeys.every(key => {
          let camelCaseKey = $util.toCamelCase(key)
          if (camelCaseKey === 'masterCategoryTypeId') {
            camelCaseKey = 'categoryTypeId'
          }

          return isFalsyOrEmpty(attributes.value[camelCaseKey])
        })
        if (hasNoCategorizedOptions) {
          setAttributes('categoryId', null, false)
        }
        if (emitChangeEvent) {
          context.emit('change', attributes.value)
        }
        return
      }
      if (categoryId) {
        setAttributes('categoryId', categoryId, false)
      }
      if (emitChangeEvent) {
        context.emit('change', attributes.value)
      }
    }
    function setAttributes(key: string, value, emitChangeEvent: boolean = true): void {
      const newKey = key === CATEGORY_TYPE_DB_KEY ? CATEGORY_TYPE_QUERY_KEY : key
      const newValue = key === CATEGORY_TYPE_DB_KEY ? value?.[0] ?? [] : value
      context.emit('change-attributes', {
        key: newKey,
        value: newValue,
        emitChangeEvent
      })
    }

    return {
      switchSearchConditionPosition,
      emphasizeConditionOptions,
      isShowEmphasizeConditionOption
    }
  }
})

export default serviceSearchFormV2
